import { toast } from "react-toastify";
// BatchManagementPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import batchService from '../../../shared/api/animal/batchService';
import livestockService from '../../../shared/api/animal/livestockService';

import inventoryService from '../../../shared/api/animal/inventoryService';
import labourLogService from '../../../shared/api/labourLogService';
import { listVaccineTasks, nextVaccineTask } from '../../../shared/api/animal/vaccineService';
import { listMedicineLogs } from '../../../shared/api/animal/medicineService';

// -------------------- Helpers --------------------
const toNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

const fmtINR = (v) => {
  const n = toNum(v);
  if (n === null) return 'N/A';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `₹${n.toFixed(0)}`;
  }
};

const fmtKg = (v, digits = 1) => {
  const n = toNum(v);
  return n === null ? '—' : `${n.toFixed(digits)} kg`;
};

const toTitle = (value) => {
  if (!value) return '—';
  return String(value)
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const daysSince = (value) => {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return Math.max(0, Math.floor((start - target) / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
};

const daysAgoLabel = (value) => {
  const diff = daysSince(value);
  if (diff === null) return 'Not recorded';
  if (diff === 0) return 'Today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
};

// try to pick a “money-like” value from different backend shapes
const pickMoney = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  const keys = [
    'totalCost',
    'cost',
    'amount',
    'totalAmount',
    'totalPrice',
    'price',
    'salaryPaid',
    'salary',
    'wage',
    'totalSalary',
    'netSalary',
    'paidAmount',
    'costInr',
    'amountInr',
    'netPayable',
    'payable',
    'finalAmount',
    'grandTotal',
    'lineTotal',
  ];
  for (const k of keys) {
    const n = toNum(obj[k]);
    if (n !== null) return n;
  }
  return null;
};

const pickISO = (obj) => {
  const keys = [
    'date',
    'feedDate',
    'logDate',
    'usageDate',
    'createdAt',
    'updatedAt',
    'givenDate',
    'actionDate',
    'fromDate',
    'toDate',
    'dueDate',
    'scheduledDate',
    'completedAt',
    'completedDate',
    'lastWeightDate',
  ];
  for (const k of keys) {
    if (obj?.[k]) return obj[k];
  }
  return null;
};

const isCompletedStatus = (s) => {
  const x = String(s || '').toUpperCase();
  return x.includes('COMPLETE') || x.includes('DONE') || x === 'COMPLETED';
};

const normalizeStatus = (s) => String(s || '').trim().toUpperCase();

const apiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  if (err?.message) return `${fallback} (${err.message})`;
  return fallback;
};


// ---- inventory price helpers ----
const pickUnitPrice = (item) => {
  if (!item) return null;
  const keys = [
    'unitPrice',
    'pricePerUnit',
    'costPerUnit',
    'rate',
    'purchasePrice',
    'avgCost',
    'price',
    'mrp',
  ];
  for (const k of keys) {
    const n = toNum(item[k]);
    if (n !== null) return n;
  }
  return null;
};

const normUnit = (u) => String(u || '').trim().toUpperCase();

const convertQtyToItemUnit = (qty, usageUnit, itemUnit) => {
  const q = toNum(qty);
  if (q === null) return null;

  const uU = normUnit(usageUnit);
  const iU = normUnit(itemUnit);

  if (iU === 'KG' && (uU === 'GRAM' || uU === 'G')) return q / 1000;
  if ((iU === 'GRAM' || iU === 'G') && uU === 'KG') return q * 1000;

  if (iU === 'LITRE' && (uU === 'ML' || uU === 'MILLILITRE')) return q / 1000;
  if ((iU === 'ML' || iU === 'MILLILITRE') && uU === 'LITRE') return q * 1000;

  return q;
};

// ---- labour compute fallback ----
const daysInclusive = (fromDate, toDate) => {
  try {
    const f = fromDate ? new Date(fromDate) : null;
    const t = toDate ? new Date(toDate) : null;
    if (!f || !t || Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return null;
    const f0 = new Date(f.getFullYear(), f.getMonth(), f.getDate());
    const t0 = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    const diff = Math.floor((t0 - f0) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff + 1 : null;
  } catch {
    return null;
  }
};

const computePerDaySalary = (log) => {
  const perDay = toNum(log?.perDaySalary);
  if (perDay !== null) return perDay;

  const type = String(log?.salaryType || '').toUpperCase();
  const amt = toNum(log?.salaryAmount);
  if (amt === null) return null;

  if (type === 'DAILY') return amt;
  if (type === 'WEEKLY') return amt / 7;
  if (type === 'MONTHLY') return amt / 30;

  return null;
};

const computeLabourTotalFromLog = (log) => {
  const perDay = computePerDaySalary(log);
  if (perDay === null) return null;

  const d1 = log?.date || log?.workDate || null;
  const from = log?.fromDate || d1;
  const to = log?.toDate || d1;

  const days = daysInclusive(from, to) ?? 1;
  const leaveDays = Math.max(0, toNum(log?.leaveDays) ?? 0);
  const payableDays = Math.max(0, days - leaveDays);

  return perDay * payableDays;
};

// ---- weight helpers ----
const getTrendClass = (trend) => {
  switch ((trend || '').toUpperCase()) {
    case 'GAIN':
      return 'trend gain';
    case 'LOSS':
      return 'trend loss';
    case 'STABLE':
      return 'trend stable';
    case 'FIRST_RECORD':
      return 'trend first';
    default:
      return 'trend muted';
  }
};

const renderChangeText = (value, trend) => {
  const n = toNum(value);
  if (n === null) {
    if ((trend || '').toUpperCase() === 'FIRST_RECORD') return 'First record';
    return 'No change data';
  }
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)} kg`;
};

const getWeightAlertType = (item, overdueDays = 7) => {
  const trend = (item?.weightTrend || '').toUpperCase();
  const diff = daysSince(item?.lastWeightDate);

  if (!item?.lastWeightDate || (item?.weightRecordCount || 0) === 0) return 'NO_DATA';
  if (trend === 'LOSS') return 'LOSS';
  if (diff !== null && diff > overdueDays) return 'OVERDUE';
  if (trend === 'STABLE') return 'STABLE';
  if (trend === 'GAIN') return 'GAIN';
  if (trend === 'FIRST_RECORD') return 'NEW';
  return 'NORMAL';
};

const getLivestockStatusClass = (item) => {
  const status = (item?.currentStatus || (item?.sold ? 'SOLD' : 'AVAILABLE')).toLowerCase();
  return ['sold', 'available', 'culled', 'deceased'].includes(status) ? status : 'available';
};

const getLivestockHealthClass = (item) => {
  return (item?.healthStatus || 'HEALTHY').toLowerCase();
};

const buildBatchWeightMetrics = (livestock = [], overdueDays = 7) => {
  const rows = Array.isArray(livestock) ? livestock : [];
  const count = rows.length;

  const totalAnimals = rows.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);

  const weightRows = rows.filter((item) => toNum(item.weight) !== null);
  const avgWeight =
    weightRows.length > 0
      ? weightRows.reduce((sum, item) => sum + Number(item.weight), 0) / weightRows.length
      : null;

  const gainRows = rows.filter((item) => toNum(item.totalWeightGainKg) !== null);
  const avgTotalGain =
    gainRows.length > 0
      ? gainRows.reduce((sum, item) => sum + Number(item.totalWeightGainKg), 0) / gainRows.length
      : null;

  const changeRows = rows.filter((item) => toNum(item.latestWeightChangeKg) !== null);
  const avgLatestChange =
    changeRows.length > 0
      ? changeRows.reduce((sum, item) => sum + Number(item.latestWeightChangeKg), 0) / changeRows.length
      : null;

  const overdueCount = rows.filter((item) => {
    const diff = daysSince(item.lastWeightDate);
    return diff !== null && diff > overdueDays;
  }).length;

  const noDataCount = rows.filter((item) => !item.lastWeightDate || (item.weightRecordCount || 0) === 0).length;
  const lossCount = rows.filter((item) => (item.weightTrend || '').toUpperCase() === 'LOSS').length;
  const stableCount = rows.filter((item) => (item.weightTrend || '').toUpperCase() === 'STABLE').length;
  const gainCount = rows.filter((item) => (item.weightTrend || '').toUpperCase() === 'GAIN').length;

  const highestWeight = [...weightRows].sort((a, b) => Number(b.weight) - Number(a.weight))[0] || null;
  const lowestWeight = [...weightRows].sort((a, b) => Number(a.weight) - Number(b.weight))[0] || null;

  const mostRecent = [...rows]
    .filter((item) => item.lastWeightDate)
    .sort((a, b) => new Date(b.lastWeightDate) - new Date(a.lastWeightDate))[0] || null;

  const alertRows = rows
    .map((item) => ({
      ...item,
      weightAlertType: getWeightAlertType(item, overdueDays),
      daysSinceLastWeight: daysSince(item.lastWeightDate),
    }))
    .filter((item) => ['LOSS', 'OVERDUE', 'NO_DATA'].includes(item.weightAlertType))
    .sort((a, b) => {
      const priority = { LOSS: 1, OVERDUE: 2, NO_DATA: 3 };
      return (priority[a.weightAlertType] || 99) - (priority[b.weightAlertType] || 99);
    });

  return {
    count,
    totalAnimals,
    avgWeight,
    avgTotalGain,
    avgLatestChange,
    overdueCount,
    noDataCount,
    lossCount,
    stableCount,
    gainCount,
    highestWeight,
    lowestWeight,
    mostRecent,
    alertRows,
  };
};

// -------------------- MODERN STYLES --------------------
const Styles = () => (
  <style>{`
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .page-title {
      font-size: 1.875rem;
      font-weight: 700;
      color: #F7FAFC;
      margin: 0;
      background: linear-gradient(135deg, #F7FAFC 0%, #A0AEC0 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .btn {
      padding: 0.625rem 1.25rem;
      border-radius: 10px;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s ease;
      font-size: 0.875rem;
      border: none;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      white-space: nowrap;
    }

    .btn-primary {
      background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
      color: white;
      box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
    }

    .btn-primary:hover {
      background: linear-gradient(135deg, #4F46E5 0%, #4338CA 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
    }

    .btn-secondary {
      background: rgba(45, 55, 72, 0.8);
      color: #E2E8F0;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .btn-secondary:hover {
      background: rgba(55, 65, 81, 0.9);
      border-color: rgba(255, 255, 255, 0.2);
    }

    .btn-danger {
      background: rgba(239, 68, 68, 0.16);
      color: #FCA5A5;
      border: 1px solid rgba(239, 68, 68, 0.28);
    }

    .btn-danger:hover {
      background: rgba(239, 68, 68, 0.24);
      transform: translateY(-1px);
    }

    .category-tabs {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      padding: 0.35rem;
      border-radius: 14px;
      background: rgba(15, 23, 42, 0.68);
      border: 1px solid rgba(255,255,255,0.08);
      margin-bottom: 1rem;
      flex-wrap: wrap;
    }

    .category-tab {
      border: none;
      cursor: pointer;
      border-radius: 11px;
      padding: 0.62rem 1rem;
      background: transparent;
      color: #94A3B8;
      font-weight: 800;
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
      transition: all 0.18s ease;
    }

    .category-tab:hover {
      background: rgba(255,255,255,0.06);
      color: #E2E8F0;
    }

    .category-tab.active {
      background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
      color: #FFFFFF;
      box-shadow: 0 8px 22px rgba(99,102,241,0.24);
    }

    .bulk-toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 0.9rem 1rem;
      margin-bottom: 1rem;
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(30, 37, 54, 0.62);
      flex-wrap: wrap;
    }

    .bulk-count {
      color: #CBD5E1;
      font-weight: 800;
      font-size: 0.9rem;
    }

    .record-pill {
      background: rgba(255,255,255,0.08);
      border: 1px solid rgba(255,255,255,0.10);
      color: inherit;
      padding: 0.12rem 0.48rem;
      border-radius: 999px;
      font-size: 0.72rem;
      font-weight: 900;
    }

    .select-checkbox {
      width: 18px;
      height: 18px;
      accent-color: #6366F1;
      cursor: pointer;
    }

    .icon-inline {
      width: 18px;
      height: 18px;
      display: inline-block;
    }

    .btn-ghost {
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.10);
      color: #E2E8F0;
    }

    .btn-ghost:hover {
      background: rgba(255,255,255,0.10);
      transform: translateY(-1px);
    }

    .table-container {
      background: linear-gradient(145deg, #1E2536 0%, #1A1F2E 100%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      overflow-x: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table th, .data-table td {
      padding: 1rem 1.5rem;
      text-align: left;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
      white-space: nowrap;
      vertical-align: middle;
    }

    .data-table th {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #64748B;
      font-weight: 600;
      background: rgba(30, 37, 54, 0.5);
    }

    .data-table td {
      font-weight: 500;
      color: #E2E8F0;
    }

    .data-table tbody tr {
      transition: all 0.15s ease;
    }

    .data-table tbody tr:hover {
      background: rgba(99, 102, 241, 0.08);
    }

    .data-table tr:last-child td {
      border-bottom: none;
    }

    .action-buttons {
      display: flex;
      gap: 0.5rem;
    }

    .action-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      color: #64748B;
    }

    .action-btn:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .action-btn.edit svg { color: #60A5FA; }
    .action-btn.delete svg { color: #F87171; }
    .action-btn.edit:hover { background: rgba(96, 165, 250, 0.15); transform: scale(1.1); }
    .action-btn.delete:hover { background: rgba(248, 113, 113, 0.15); transform: scale(1.1); }

    .manage-col {
      display: flex;
      gap: 0.6rem;
      align-items: center;
    }

    .btn-view-livestock {
      color: #A78BFA;
      background: rgba(167, 139, 250, 0.12);
      padding: 0.5rem 1rem;
      font-size: 0.8rem;
      font-weight: 600;
      border-radius: 8px;
      text-decoration: none;
      transition: all 0.2s ease;
      border: 1px solid rgba(167, 139, 250, 0.25);
    }

    .btn-view-livestock:hover {
      background: rgba(167, 139, 250, 0.25);
      transform: translateY(-1px);
    }

    .btn-details {
      color: #34D399;
      background: rgba(52, 211, 153, 0.10);
      padding: 0.5rem 1rem;
      font-size: 0.8rem;
      font-weight: 700;
      border-radius: 8px;
      transition: all 0.2s ease;
      border: 1px solid rgba(52, 211, 153, 0.22);
    }

    .btn-details:hover {
      background: rgba(52, 211, 153, 0.18);
      transform: translateY(-1px);
    }

    .badge {
      padding: 0.35rem 0.85rem;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: capitalize;
      display: inline-block;
    }

    .badge.Active { background: rgba(16, 185, 129, 0.15); color: #34D399; border: 1px solid rgba(16, 185, 129, 0.3); }
    .badge.Upcoming { background: rgba(245, 158, 11, 0.15); color: #FBBF24; border: 1px solid rgba(245, 158, 11, 0.3); }
    .badge.Completed { background: rgba(239, 68, 68, 0.15); color: #F87171; border: 1px solid rgba(239, 68, 68, 0.3); }
    .badge.Inactive { background: rgba(148, 163, 184, 0.12); color: #CBD5E1; border: 1px solid rgba(148, 163, 184, 0.25); margin-left: 0.5rem; }

    .badge.available { background: rgba(16,185,129,0.15); color: #34D399; border: 1px solid rgba(16,185,129,0.3); }
    .badge.sold { background: rgba(239,68,68,0.15); color: #F87171; border: 1px solid rgba(239,68,68,0.3); }
    .badge.culled { background: rgba(245,158,11,0.15); color: #FBBF24; border: 1px solid rgba(245,158,11,0.3); }
    .badge.deceased { background: rgba(100,116,139,0.18); color: #CBD5E1; border: 1px solid rgba(100,116,139,0.35); }

    .badge.healthy { background: rgba(16,185,129,0.12); color: #34D399; border: 1px solid rgba(16,185,129,0.28); }
    .badge.sick { background: rgba(239,68,68,0.12); color: #F87171; border: 1px solid rgba(239,68,68,0.28); }
    .badge.in_treatment { background: rgba(59,130,246,0.12); color: #60A5FA; border: 1px solid rgba(59,130,246,0.28); }
    .badge.recovering { background: rgba(245,158,11,0.12); color: #FBBF24; border: 1px solid rgba(245,158,11,0.28); }
    .badge.quarantined { background: rgba(168,85,247,0.12); color: #C084FC; border: 1px solid rgba(168,85,247,0.28); }
    .badge.injured { background: rgba(244,63,94,0.12); color: #FB7185; border: 1px solid rgba(244,63,94,0.28); }
    .badge.dead { background: rgba(100,116,139,0.12); color: #94A3B8; border: 1px solid rgba(100,116,139,0.28); }

    .trend {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      font-weight: 800;
      font-size: 0.78rem;
    }

    .trend.gain { color: #34D399; }
    .trend.loss { color: #F87171; }
    .trend.stable { color: #FBBF24; }
    .trend.first { color: #A78BFA; }
    .trend.muted { color: #94A3B8; }

    .progress-container {
      background: rgba(45, 55, 72, 0.8);
      border-radius: 8px;
      height: 8px;
      width: 100%;
      overflow: hidden;
    }

    .progress-bar {
      height: 100%;
      background: linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%);
      border-radius: 8px;
      transition: width 0.5s ease-in-out;
      box-shadow: 0 0 10px rgba(99, 102, 241, 0.5);
    }

    .modal-overlay {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(8px);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
      padding: 1rem;
      animation: fadeIn 0.2s ease-out;
    }

    .modal-content {
      background: linear-gradient(145deg, #1E2536 0%, #1A1F2E 100%);
      color: #F7FAFC;
      border-radius: 20px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 2rem;
      width: 100%;
      max-width: 640px;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
      animation: slideUp 0.3s ease-out;
    }

    .details-modal {
      max-width: 1180px;
      max-height: 88vh;
      overflow: auto;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 1.25rem;
      gap: 1rem;
    }

    .modal-title {
      font-size: 1.35rem;
      font-weight: 800;
      margin: 0;
      background: linear-gradient(135deg, #F7FAFC 0%, #A0AEC0 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .modal-subtitle {
      color: #94A3B8;
      font-size: 0.9rem;
      margin-top: 0.25rem;
      font-weight: 600;
    }

    .close-btn {
      background: rgba(255, 255, 255, 0.05);
      border: none;
      color: #64748B;
      font-size: 1.75rem;
      cursor: pointer;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      flex-shrink: 0;
    }

    .close-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #F7FAFC;
      transform: rotate(90deg);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.25rem;
    }

    .form-group {
      margin-bottom: 0.5rem;
    }

    .form-group label {
      display: block;
      font-size: 0.875rem;
      color: #94A3B8;
      margin-bottom: 0.5rem;
      font-weight: 600;
    }

    .form-input, .form-textarea, .form-select {
      width: 100%;
      background: rgba(30, 37, 54, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      padding: 0.75rem 1rem;
      color: #F7FAFC;
      font-size: 0.95rem;
      transition: all 0.2s ease;
    }

    .form-input:focus, .form-textarea:focus, .form-select:focus {
      outline: none;
      border-color: #6366F1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
    }

    .form-input::placeholder { color: #64748B; }
    .form-textarea { resize: vertical; min-height: 90px; }

    .toggle-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.75rem 1rem;
      border-radius: 12px;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(30, 37, 54, 0.55);
    }

    .toggle-row .toggle-text {
      color: #E2E8F0;
      font-weight: 800;
      font-size: 0.95rem;
    }

    .switch {
      position: relative;
      width: 48px;
      height: 28px;
      display: inline-block;
    }

    .switch input { display: none; }

    .slider {
      position: absolute;
      cursor: pointer;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(148, 163, 184, 0.35);
      transition: .2s;
      border-radius: 999px;
      border: 1px solid rgba(255,255,255,0.08);
    }

    .slider:before {
      position: absolute;
      content: "";
      height: 22px;
      width: 22px;
      left: 3px;
      top: 2px;
      background: #E2E8F0;
      transition: .2s;
      border-radius: 999px;
    }

    input:checked + .slider {
      background: rgba(16, 185, 129, 0.35);
    }

    input:checked + .slider:before {
      transform: translateX(20px);
      background: #34D399;
    }

    .error-message {
      background: rgba(239, 68, 68, 0.12);
      color: #F87171;
      padding: 1rem;
      border-radius: 12px;
      margin-bottom: 1rem;
      text-align: center;
      border: 1px solid rgba(239, 68, 68, 0.3);
    }

    .empty-state {
      text-align: center;
      padding: 4rem 2rem;
      color: #64748B;
    }

    .empty-state h3 {
      color: #94A3B8;
      margin-bottom: 0.5rem;
    }

    .details-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;
      margin-bottom: 1.2rem;
    }

    .details-card {
      border-radius: 16px;
      border: 1px solid rgba(255,255,255,0.10);
      background: rgba(30, 37, 54, 0.55);
      padding: 1rem;
      min-width: 0;
    }

    .details-card .k {
      color: #94A3B8;
      font-size: 0.78rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-weight: 800;
    }

    .details-card .v {
      margin-top: 0.35rem;
      font-size: 1.15rem;
      font-weight: 900;
      color: #F7FAFC;
      line-height: 1.25;
    }

    .details-card .s {
      margin-top: 0.25rem;
      color: #CBD5E1;
      font-size: 0.85rem;
      font-weight: 600;
      opacity: 0.9;
      line-height: 1.45;
    }

    .section {
      border-radius: 18px;
      border: 1px solid rgba(255,255,255,0.10);
      background: rgba(30, 37, 54, 0.45);
      padding: 1.25rem;
      margin-top: 1rem;
    }

    .section-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 0.8rem;
      flex-wrap: wrap;
    }

    .section-title h3 {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 900;
      color: #F7FAFC;
    }

    .pill {
      padding: 0.35rem 0.75rem;
      border-radius: 999px;
      font-size: 0.78rem;
      font-weight: 900;
      border: 1px solid rgba(255,255,255,0.10);
      color: #E2E8F0;
      background: rgba(255,255,255,0.06);
    }

    .kv {
      display: grid;
      grid-template-columns: 220px 1fr;
      gap: 0.6rem 1rem;
      align-items: center;
      margin-top: 0.5rem;
    }

    .kv .key {
      color: #94A3B8;
      font-weight: 800;
      font-size: 0.9rem;
    }

    .kv .val {
      color: #E2E8F0;
      font-weight: 700;
      font-size: 0.95rem;
      overflow-wrap: anywhere;
    }

    .mini-note {
      margin-top: 0.9rem;
      color: #94A3B8;
      font-size: 0.85rem;
      font-weight: 600;
      opacity: 0.95;
    }

    .weight-panels {
      display: grid;
      grid-template-columns: 0.95fr 1.25fr;
      gap: 1rem;
      margin-top: 0.8rem;
    }

    .weight-panel {
      border-radius: 16px;
      border: 1px solid rgba(255,255,255,0.10);
      background: rgba(15, 20, 25, 0.42);
      padding: 1rem;
      min-width: 0;
    }

    .weight-panel h4 {
      margin: 0 0 0.85rem 0;
      font-size: 0.95rem;
      font-weight: 900;
      color: #F7FAFC;
    }

    .alert-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      max-height: 420px;
      overflow: auto;
    }

    .alert-item {
      border-radius: 14px;
      padding: 0.85rem 0.95rem;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(255,255,255,0.03);
    }

    .alert-item.loss {
      background: rgba(239,68,68,0.10);
      border-color: rgba(239,68,68,0.22);
    }

    .alert-item.overdue {
      background: rgba(245,158,11,0.10);
      border-color: rgba(245,158,11,0.22);
    }

    .alert-item.no_data {
      background: rgba(100,116,139,0.12);
      border-color: rgba(100,116,139,0.22);
    }

    .alert-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    .alert-name {
      font-weight: 900;
      color: #F8FAFC;
    }

    .alert-meta {
      margin-top: 0.35rem;
      color: #CBD5E1;
      font-size: 0.82rem;
      line-height: 1.45;
    }

    .alert-pill {
      padding: 0.28rem 0.7rem;
      border-radius: 999px;
      font-size: 0.72rem;
      font-weight: 900;
      border: 1px solid rgba(255,255,255,0.10);
      display: inline-block;
    }

    .alert-pill.loss {
      background: rgba(239,68,68,0.15);
      color: #FCA5A5;
      border-color: rgba(239,68,68,0.3);
    }

    .alert-pill.overdue {
      background: rgba(245,158,11,0.15);
      color: #FCD34D;
      border-color: rgba(245,158,11,0.3);
    }

    .alert-pill.no_data {
      background: rgba(100,116,139,0.18);
      color: #CBD5E1;
      border-color: rgba(100,116,139,0.3);
    }

    .weight-table-wrap {
      overflow: auto;
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,0.08);
    }

    .weight-table {
      width: 100%;
      border-collapse: collapse;
    }

    .weight-table th,
    .weight-table td {
      padding: 0.85rem 0.9rem;
      border-bottom: 1px solid rgba(255,255,255,0.06);
      text-align: left;
      white-space: nowrap;
      vertical-align: middle;
    }

    .weight-table th {
      background: rgba(25, 33, 49, 0.9);
      color: #94A3B8;
      font-size: 0.74rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      position: sticky;
      top: 0;
      z-index: 1;
    }

    .weight-table td {
      color: #E2E8F0;
      font-size: 0.87rem;
    }

    .weight-table tr:last-child td {
      border-bottom: none;
    }

    .cell-stack {
      display: flex;
      flex-direction: column;
      gap: 0.18rem;
    }

    .primary-text {
      color: #F8FAFC;
      font-weight: 700;
    }

    .secondary-text {
      color: #94A3B8;
      font-size: 0.78rem;
      line-height: 1.4;
    }

    .mini-action-btn {
      background: rgba(99,102,241,0.12);
      color: #C7D2FE;
      border: 1px solid rgba(99,102,241,0.22);
      border-radius: 10px;
      padding: 0.45rem 0.75rem;
      font-size: 0.78rem;
      font-weight: 800;
      cursor: pointer;
    }

    .mini-action-btn:hover {
      background: rgba(99,102,241,0.2);
    }

    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

    .skeleton-bar {
      background: linear-gradient(90deg, #2D3748 25%, #3D4A5C 50%, #2D3748 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 6px;
    }

    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    @media (max-width: 1100px) {
      .weight-panels {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 980px) {
      .details-grid {
        grid-template-columns: 1fr 1fr;
      }
    }

    @media (max-width: 768px) {
      .form-grid {
        grid-template-columns: 1fr;
      }

      .page-header {
        flex-direction: column;
        align-items: flex-start;
      }

      .details-grid {
        grid-template-columns: 1fr;
      }

      .kv {
        grid-template-columns: 1fr;
      }
    }
  `}</style>
);

// -------------------- SVG ICONS --------------------
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '18px', height: '18px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '18px', height: '18px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
);


const ViewIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '18px', height: '18px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.644C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.437 0 .644C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const DetailsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '18px', height: '18px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875h16.5M3.75 9.75h16.5M3.75 14.625h16.5M3.75 19.5h16.5" />
  </svg>
);

// -------------------- Overall Details Modal --------------------
const OverallDetailsModal = ({ isOpen, onClose, batch, onOpenLivestockPage }) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [vaccines, setVaccines] = useState({
    total: 0,
    completed: 0,
    overdue: 0,
    upcoming: 0,
    next: null,
    lastDone: null,
  });

  const [feed, setFeed] = useState({
    usageCount: 0,
    totalQty: null,
    unitHint: null,
    totalCost: null,
    lastDate: null,
    topItems: [],
  });

  const [medicine, setMedicine] = useState({
    logsCount: 0,
    invUsageCount: 0,
    totalCost: null,
    lastDate: null,
  });

  const [labour, setLabour] = useState({
    logsCount: 0,
    totalCost: null,
    workersCount: 0,
    leaveDays: null,
    lastDate: null,
  });

  const [livestock, setLivestock] = useState([]);

  const batchId = batch?.id;

  const loadAll = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setErr('');

    try {
      const [
        vaccineTasksRes,
        nextTaskRes,
        usageRes,
        medicineLogsRes,
        labourRes,
        inventoryItemsRes,
        livestockRes,
      ] = await Promise.allSettled([
        listVaccineTasks(batchId),
        nextVaccineTask(batchId),
        inventoryService.getUsageByBatch(batchId),
        listMedicineLogs({ batchId }),
        labourLogService.getAllLabourLogs({ batchId, size: 1000 }),
        inventoryService.getAllInventory(),
        livestockService.getByBatchId(batchId),
      ]);

      let inventoryItems = [];
      if (inventoryItemsRes.status === 'fulfilled') {
        const data = inventoryItemsRes.value;
        inventoryItems = Array.isArray(data) ? data : (data?.content ?? []);
      }
      const invById = new Map(inventoryItems.map((it) => [Number(it?.id), it]));

      let tasks = [];
      if (vaccineTasksRes.status === 'fulfilled') {
        const data = vaccineTasksRes.value;
        tasks = Array.isArray(data) ? data : (data?.content ?? []);
      }

      let livestockData = [];
      if (livestockRes.status === 'fulfilled') {
        const data = livestockRes.value;
        livestockData = Array.isArray(data) ? data : (data?.content ?? []);
      }
      setLivestock(livestockData);

      const now = new Date();
      let total = tasks.length;
      let completed = 0;
      let overdue = 0;
      let upcoming = 0;
      let lastDone = null;

      tasks.forEach((t) => {
        const st = normalizeStatus(t?.status);
        const done = isCompletedStatus(st);
        if (done) completed++;

        const due = t?.dueDate || t?.scheduledDate || t?.date;
        const dueDt = due ? new Date(due) : null;

        if (done) {
          const doneAt = t?.completedAt || t?.completedDate || t?.updatedAt || t?.date;
          if (doneAt) {
            const dt = new Date(doneAt);
            if (!lastDone || dt > lastDone) lastDone = dt;
          }
        } else {
          if (dueDt && dueDt < now) overdue++;
          else upcoming++;
        }
      });

      let nextTask = null;
      if (nextTaskRes.status === 'fulfilled') nextTask = nextTaskRes.value || null;

      setVaccines({
        total,
        completed,
        overdue,
        upcoming,
        next: nextTask,
        lastDone: lastDone ? lastDone.toISOString() : null,
      });

      let usage = [];
      if (usageRes.status === 'fulfilled') {
        const data = usageRes.value;
        usage = Array.isArray(data) ? data : (data?.content ?? []);
      }

      const isFeedUsage = (u) => {
        const t = normalizeStatus(u?.itemType || u?.type);
        const name = String(u?.inventoryName || u?.itemName || '').toLowerCase();
        const reason = String(u?.reason || '').toLowerCase();
        return (
          t === 'FEED' ||
          reason.includes('feed') ||
          reason.includes('feeding') ||
          name.includes('feed')
        );
      };

      const isMedicineUsage = (u) => {
        const t = normalizeStatus(u?.itemType || u?.type);
        const name = String(u?.inventoryName || u?.itemName || '').toLowerCase();
        const reason = String(u?.reason || '').toLowerCase();
        return (
          t === 'MEDICINE' ||
          reason.includes('medicine') ||
          reason.includes('treatment') ||
          name.includes('medicine') ||
          name.includes('vaccine') ||
          name.includes('injection')
        );
      };

      const feedUsage = usage.filter(isFeedUsage);
      const medUsage = usage.filter(isMedicineUsage);

      let feedQty = 0;
      let feedQtySeen = false;
      let unitHint = null;
      let feedCost = 0;
      let feedCostSeen = false;
      let feedLast = null;

      const itemAgg = new Map();

      feedUsage.forEach((u) => {
        const qty =
          toNum(u?.quantityUsed) ??
          toNum(u?.qty) ??
          toNum(u?.quantity) ??
          toNum(u?.deductedGrams) ??
          toNum(u?.requiredGrams);

        if (qty !== null) {
          feedQty += qty;
          feedQtySeen = true;
        }

        let m = pickMoney(u);
        if (m === null) {
          const invId = toNum(u?.inventoryId) ?? toNum(u?.inventory?.id);
          const item = invId ? invById.get(Number(invId)) : null;

          const unitPrice = pickUnitPrice(item);
          const usageUnit = u?.inventoryUnit || u?.unit || u?.quantityUnit;
          const itemUnit = item?.unit || item?.inventoryUnit || item?.baseUnit;

          const qtyInItemUnit = convertQtyToItemUnit(qty, usageUnit, itemUnit);

          if (unitPrice !== null && qtyInItemUnit !== null) {
            m = qtyInItemUnit * unitPrice;
          }
        }

        if (m !== null) {
          feedCost += m;
          feedCostSeen = true;
        }

        const d = pickISO(u);
        if (d) {
          const dt = new Date(d);
          if (!feedLast || dt > feedLast) feedLast = dt;
        }

        unitHint = unitHint || u?.unit || u?.inventoryUnit || u?.quantityUnit || null;

        const nm = u?.inventoryName || u?.itemName || 'Feed';
        const prev = itemAgg.get(nm) || { qty: 0, cost: 0 };
        itemAgg.set(nm, {
          qty: prev.qty + (qty ?? 0),
          cost: prev.cost + (m ?? 0),
        });
      });

      if (feedUsage.length === 0) {
        feedCost = 0;
        feedCostSeen = true;
      }

      const topItems = Array.from(itemAgg.entries())
        .sort((a, b) => (b[1].qty || 0) - (a[1].qty || 0))
        .slice(0, 3)
        .map(([name, v]) => ({
          name,
          qty: v.qty,
          cost: v.cost,
        }));

      setFeed({
        usageCount: feedUsage.length,
        totalQty: feedQtySeen ? feedQty : null,
        unitHint,
        totalCost: feedCostSeen ? feedCost : null,
        lastDate: feedLast ? feedLast.toISOString() : null,
        topItems,
      });

      let medLogs = [];
      if (medicineLogsRes.status === 'fulfilled') {
        const data = medicineLogsRes.value;
        medLogs = Array.isArray(data) ? data : (data?.content ?? []);
      }

      let medCost = 0;
      let medCostSeen = false;
      let medLast = null;

      const scanDates = (obj) => {
        const d = pickISO(obj);
        if (!d) return;
        const dt = new Date(d);
        if (!medLast || dt > medLast) medLast = dt;
      };

      medLogs.forEach((m) => {
        const money = pickMoney(m);
        if (money !== null) {
          medCost += money;
          medCostSeen = true;
        }
        scanDates(m);
      });

      medUsage.forEach((u) => {
        let money = pickMoney(u);

        if (money === null) {
          const qty =
            toNum(u?.quantityUsed) ??
            toNum(u?.qty) ??
            toNum(u?.quantity) ??
            toNum(u?.deductedGrams) ??
            toNum(u?.requiredGrams);

          const invId = toNum(u?.inventoryId) ?? toNum(u?.inventory?.id);
          const item = invId ? invById.get(Number(invId)) : null;

          const unitPrice = pickUnitPrice(item);
          const usageUnit = u?.inventoryUnit || u?.unit || u?.quantityUnit;
          const itemUnit = item?.unit || item?.inventoryUnit || item?.baseUnit;

          const qtyInItemUnit = convertQtyToItemUnit(qty, usageUnit, itemUnit);

          if (unitPrice !== null && qtyInItemUnit !== null) {
            money = qtyInItemUnit * unitPrice;
          }
        }

        if (money !== null) {
          medCost += money;
          medCostSeen = true;
        }
        scanDates(u);
      });

      if ((medLogs.length + medUsage.length) === 0) {
        medCost = 0;
        medCostSeen = true;
      }

      setMedicine({
        logsCount: medLogs.length,
        invUsageCount: medUsage.length,
        totalCost: medCostSeen ? medCost : null,
        lastDate: medLast ? medLast.toISOString() : null,
      });

      let workLogs = [];
      if (labourRes.status === 'fulfilled') {
        const data = labourRes.value;
        workLogs = Array.isArray(data) ? data : (data?.content ?? []);
      }

      const filtered = workLogs.filter((w) => {
        if (w?.batchId) return Number(w.batchId) === Number(batchId);
        if (Array.isArray(w?.batchIds)) return w.batchIds.map(Number).includes(Number(batchId));
        return true;
      });

      let totalLab = 0;
      let totalLabSeen = false;
      let lastLab = null;
      let leaveDays = 0;
      let leaveSeen = false;
      const workers = new Set();

      filtered.forEach((w) => {
        let money = pickMoney(w);
        if (money === null) {
          money = computeLabourTotalFromLog(w);
        }

        if (money !== null) {
          totalLab += money;
          totalLabSeen = true;
        }

        const ld = toNum(w?.leaveDays);
        if (ld !== null) {
          leaveDays += ld;
          leaveSeen = true;
        }

        const nm =
          w?.workerName ||
          w?.name ||
          w?.worker?.name ||
          w?.worker?.workerName ||
          null;
        if (nm) workers.add(String(nm));

        const d = pickISO(w);
        if (d) {
          const dt = new Date(d);
          if (!lastLab || dt > lastLab) lastLab = dt;
        }
      });

      if (filtered.length === 0) {
        totalLab = 0;
        totalLabSeen = true;
      }

      setLabour({
        logsCount: filtered.length,
        totalCost: totalLabSeen ? totalLab : null,
        workersCount: workers.size,
        leaveDays: leaveSeen ? leaveDays : (filtered.length ? null : 0),
        lastDate: lastLab ? lastLab.toISOString() : null,
      });
    } catch (e) {
      console.error(e);
      setErr('Failed to load overall details. Check API endpoints and console logs.');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    if (!isOpen || !batchId) return;
    loadAll();
  }, [isOpen, batchId, loadAll]);

  const weightMetrics = useMemo(
    () => buildBatchWeightMetrics(livestock, 7),
    [livestock]
  );

  if (!isOpen) return null;

  const nextV = vaccines?.next;
  const nextVName =
    nextV?.vaccineName || nextV?.name || nextV?.title || nextV?.taskName || 'Next Vaccine Task';
  const nextVDue = nextV?.dueDate || nextV?.scheduledDate || nextV?.date || null;
  const nextVStatus = nextV?.status || null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{batch?.batchName || 'Batch'} — Overall Details</h2>
            <div className="modal-subtitle">
              Sections: Weight • Vaccines • Feed • Medicine • Labour Cost
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" onClick={loadAll} disabled={loading}>
              ⟳ Refresh
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => onOpenLivestockPage?.(batch)}
            >
              Open Livestock
            </button>
            <button onClick={onClose} className="close-btn">&times;</button>
          </div>
        </div>

        {err && <div className="error-message">{err}</div>}

        <div className="details-grid">
          <div className="details-card">
            <div className="k">Vaccine Tasks</div>
            <div className="v">{loading ? '...' : vaccines.total}</div>
            <div className="s">
              {loading ? 'Loading' : `${vaccines.completed} done • ${vaccines.overdue} overdue`}
            </div>
          </div>

          <div className="details-card">
            <div className="k">Feed Cost</div>
            <div className="v">{loading ? '...' : fmtINR(feed.totalCost)}</div>
            <div className="s">
              {loading ? 'Loading' : `${feed.usageCount} usage logs • Last: ${fmtDate(feed.lastDate)}`}
            </div>
          </div>

          <div className="details-card">
            <div className="k">Medicine Cost</div>
            <div className="v">{loading ? '...' : fmtINR(medicine.totalCost)}</div>
            <div className="s">
              {loading ? 'Loading' : `${medicine.logsCount} logs • Last: ${fmtDate(medicine.lastDate)}`}
            </div>
          </div>

          <div className="details-card">
            <div className="k">Labour Cost</div>
            <div className="v">{loading ? '...' : fmtINR(labour.totalCost)}</div>
            <div className="s">
              {loading ? 'Loading' : `${labour.logsCount} work logs • Workers: ${labour.workersCount}`}
            </div>
          </div>
        </div>

        <div className="section">
          <div className="section-title">
            <h3>🐐 Weight Monitoring</h3>
            <span className="pill">
              {loading ? 'Loading…' : `${weightMetrics.lossCount} loss • ${weightMetrics.overdueCount} overdue • ${weightMetrics.noDataCount} no-data`}
            </span>
          </div>

          <div className="details-grid" style={{ marginBottom: 0 }}>
            <div className="details-card">
              <div className="k">Average Weight</div>
              <div className="v">{loading ? '...' : fmtKg(weightMetrics.avgWeight)}</div>
              <div className="s">
                Highest: {loading ? '...' : weightMetrics.highestWeight ? `${weightMetrics.highestWeight.animalId || `#${weightMetrics.highestWeight.id}`} • ${fmtKg(weightMetrics.highestWeight.weight)}` : '—'}
              </div>
            </div>

            <div className="details-card">
              <div className="k">Average Latest Change</div>
              <div className="v">
                {loading
                  ? '...'
                  : weightMetrics.avgLatestChange === null
                  ? '—'
                  : `${weightMetrics.avgLatestChange > 0 ? '+' : ''}${weightMetrics.avgLatestChange.toFixed(1)} kg`}
              </div>
              <div className="s">
                Avg total gain: {loading ? '...' : fmtKg(weightMetrics.avgTotalGain)}
              </div>
            </div>

            <div className="details-card">
              <div className="k">Animals Covered</div>
              <div className="v">{loading ? '...' : weightMetrics.totalAnimals}</div>
              <div className="s">
                {loading ? 'Loading' : `${weightMetrics.count} livestock rows`}
              </div>
            </div>

            <div className="details-card">
              <div className="k">Last Batch Update</div>
              <div className="v">{loading ? '...' : weightMetrics.mostRecent ? fmtDate(weightMetrics.mostRecent.lastWeightDate) : '—'}</div>
              <div className="s">
                {loading ? 'Loading' : weightMetrics.mostRecent ? daysAgoLabel(weightMetrics.mostRecent.lastWeightDate) : 'No weight logs'}
              </div>
            </div>
          </div>

          <div className="weight-panels">
            <div className="weight-panel">
              <h4>Critical Weight Alerts</h4>

              {loading ? (
                <div className="secondary-text">Loading alerts...</div>
              ) : !weightMetrics.alertRows.length ? (
                <div className="secondary-text">No critical weight alerts right now.</div>
              ) : (
                <div className="alert-list">
                  {weightMetrics.alertRows.map((item) => (
                    <div
                      key={item.id}
                      className={`alert-item ${item.weightAlertType.toLowerCase()}`}
                    >
                      <div className="alert-top">
                        <div className="alert-name">
                          {item.animalId || `#${item.id}`} • {toTitle(item.type)}
                        </div>
                        <span className={`alert-pill ${item.weightAlertType.toLowerCase()}`}>
                          {toTitle(item.weightAlertType)}
                        </span>
                      </div>

                      <div className="alert-meta">
                        Current: {fmtKg(item.weight)} • Change: {renderChangeText(item.latestWeightChangeKg, item.weightTrend)}
                        <br />
                        Last measured: {fmtDate(item.lastWeightDate)} {item.lastWeightDate ? `(${daysAgoLabel(item.lastWeightDate)})` : ''}
                        <br />
                        Logs: {item.weightRecordCount ?? 0} • Health: {toTitle(item.healthStatus)}
                      </div>

                      <div style={{ marginTop: '0.7rem' }}>
                        <button
                          className="mini-action-btn"
                          onClick={() => onOpenLivestockPage?.(batch, item)}
                        >
                          Open Livestock
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="weight-panel">
              <h4>Livestock Weight Snapshot</h4>

              <div className="weight-table-wrap">
                <table className="weight-table">
                  <thead>
                    <tr>
                      <th>Animal</th>
                      <th>Health</th>
                      <th>Weight</th>
                      <th>Latest Change</th>
                      <th>Total Gain</th>
                      <th>Last Measured</th>
                      <th>Logs</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      [...Array(4)].map((_, i) => (
                        <tr key={i}>
                          {[...Array(9)].map((__, j) => (
                            <td key={j}>
                              <div className="skeleton-bar" style={{ height: '18px', width: '80%' }} />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : livestock.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="secondary-text" style={{ padding: '1rem' }}>
                          No livestock found for this batch.
                        </td>
                      </tr>
                    ) : (
                      livestock.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="cell-stack">
                              <span className="primary-text">{item.animalId || `#${item.id}`}</span>
                              <span className="secondary-text">{toTitle(item.type)} • Qty: {item.quantity ?? 1}</span>
                            </div>
                          </td>

                          <td>
                            <span className={`badge ${getLivestockHealthClass(item)}`}>
                              {toTitle(item.healthStatus || 'HEALTHY')}
                            </span>
                          </td>

                          <td>{fmtKg(item.weight)}</td>

                          <td>
                            <span className={getTrendClass(item.weightTrend)}>
                              {renderChangeText(item.latestWeightChangeKg, item.weightTrend)}
                            </span>
                          </td>

                          <td>{fmtKg(item.totalWeightGainKg)}</td>

                          <td>
                            <div className="cell-stack">
                              <span className="primary-text">{fmtDate(item.lastWeightDate)}</span>
                              <span className="secondary-text">{daysAgoLabel(item.lastWeightDate)}</span>
                            </div>
                          </td>

                          <td>{item.weightRecordCount ?? 0}</td>

                          <td>
                            <span className={`badge ${getLivestockStatusClass(item)}`}>
                              {toTitle(item.currentStatus || (item.sold ? 'SOLD' : 'AVAILABLE'))}
                            </span>
                          </td>

                          <td>
                            <button
                              className="mini-action-btn"
                              onClick={() => onOpenLivestockPage?.(batch, item)}
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mini-note">
                These weight insights are computed from the livestock data returned by your updated livestock API. No extra batch weight endpoint is needed.
              </div>
            </div>
          </div>
        </div>

        <div className="section">
          <div className="section-title">
            <h3>💉 Vaccines</h3>
            <span className="pill">
              {loading ? 'Loading…' : `${vaccines.upcoming} upcoming • ${vaccines.overdue} overdue`}
            </span>
          </div>

          <div className="kv">
            <div className="key">Total tasks</div>
            <div className="val">{loading ? '...' : vaccines.total}</div>

            <div className="key">Completed</div>
            <div className="val">{loading ? '...' : vaccines.completed}</div>

            <div className="key">Last completed date</div>
            <div className="val">{loading ? '...' : fmtDate(vaccines.lastDone)}</div>

            <div className="key">Next task</div>
            <div className="val">
              {loading ? '...' : (
                <>
                  <b>{nextVName}</b>
                  {nextVDue ? <> • Due: {fmtDate(nextVDue)}</> : null}
                  {nextVStatus ? <> • Status: {String(nextVStatus)}</> : null}
                </>
              )}
            </div>
          </div>

          <div className="mini-note">
            Note: Overdue is calculated using task dueDate/scheduledDate if present.
          </div>
        </div>

        <div className="section">
          <div className="section-title">
            <h3>🌾 Feed</h3>
            <span className="pill">
              {loading ? 'Loading…' : `${feed.usageCount} usage logs`}
            </span>
          </div>

          <div className="kv">
            <div className="key">Total feed quantity used</div>
            <div className="val">
              {loading
                ? '...'
                : feed.totalQty !== null
                ? `${Number(feed.totalQty).toFixed(2)} ${feed.unitHint || ''}`.trim()
                : 'N/A'}
            </div>

            <div className="key">Total feed cost</div>
            <div className="val">{loading ? '...' : fmtINR(feed.totalCost)}</div>

            <div className="key">Last feed usage date</div>
            <div className="val">{loading ? '...' : fmtDate(feed.lastDate)}</div>

            <div className="key">Top feed items</div>
            <div className="val">
              {loading ? '...' : (feed.topItems?.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {feed.topItems.map((x, idx) => (
                    <div key={idx}>
                      • {x.name} — {Number(x.qty || 0).toFixed(2)} {feed.unitHint || ''} {x.cost ? `(${fmtINR(x.cost)})` : ''}
                    </div>
                  ))}
                </div>
              ) : 'N/A')}
            </div>
          </div>

          <div className="mini-note">
            Feed totals are computed from Inventory Usage logs (itemType=FEED OR reason/name contains “feed”). If no money fields exist, cost is computed from Inventory item unit price.
          </div>
        </div>

        <div className="section">
          <div className="section-title">
            <h3>💊 Medicine</h3>
            <span className="pill">
              {loading ? 'Loading…' : `${medicine.logsCount} logs • ${medicine.invUsageCount} inv-usage`}
            </span>
          </div>

          <div className="kv">
            <div className="key">Medicine logs count</div>
            <div className="val">{loading ? '...' : medicine.logsCount}</div>

            <div className="key">Inventory medicine usage count</div>
            <div className="val">{loading ? '...' : medicine.invUsageCount}</div>

            <div className="key">Total medicine cost</div>
            <div className="val">{loading ? '...' : fmtINR(medicine.totalCost)}</div>

            <div className="key">Last medicine activity</div>
            <div className="val">{loading ? '...' : fmtDate(medicine.lastDate)}</div>
          </div>

          <div className="mini-note">
            Cost is summed from Medicine logs + Inventory Usage (MEDICINE). If no money fields exist, cost is computed from Inventory item unit price.
          </div>
        </div>

        <div className="section" style={{ marginBottom: '0.25rem' }}>
          <div className="section-title">
            <h3>👷 Labour Cost</h3>
            <span className="pill">
              {loading ? 'Loading…' : `${labour.logsCount} work logs`}
            </span>
          </div>

          <div className="kv">
            <div className="key">Total labour cost</div>
            <div className="val">{loading ? '...' : fmtINR(labour.totalCost)}</div>

            <div className="key">Workers involved</div>
            <div className="val">{loading ? '...' : labour.workersCount}</div>

            <div className="key">Leave days (sum)</div>
            <div className="val">
              {loading ? '...' : labour.leaveDays !== null ? labour.leaveDays : 'N/A'}
            </div>

            <div className="key">Last work log date</div>
            <div className="val">{loading ? '...' : fmtDate(labour.lastDate)}</div>
          </div>

          <div className="mini-note">
            Labour cost uses work log money fields if present, otherwise it is computed using perDaySalary / salaryType+salaryAmount and leaveDays.
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.75rem', marginTop: '1.25rem' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// -------------------- FORM MODAL --------------------
const BatchFormModal = ({ isOpen, onClose, onSave, initialData }) => {
  const isEditing = !!initialData;
  const [formData, setFormData] = useState({});
  const [endDateOverridden, setEndDateOverridden] = useState(false);

  // UTC calendar arithmetic avoids browser timezone/DST shifts.
  const addCalendarDays = (isoDate, days) => {
    if (!isoDate) return '';
    const date = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '';
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  };

  const BATCH_GOALS = ['FATTENING', 'BREEDING', 'MEAT'];

  useEffect(() => {
    const fmt = (d) => (d ? new Date(d).toISOString().split('T')[0] : '');

    setEndDateOverridden(Boolean(initialData));
    setFormData({
      batchName: initialData?.batchName || '',
      startDate: fmt(initialData?.startDate),
      endDate: fmt(initialData?.endDate),
      totalPurchasePrice: initialData?.totalPurchasePrice ?? '',
      marketPricePerKg: initialData?.marketPricePerKg ?? '',
      closed: initialData?.closed ?? false,
      investorAmount: initialData?.investorAmount ?? '',
      notes: initialData?.notes || '',
      species: initialData?.species || '',
      batchGoal: initialData?.batchGoal || '',
      locationPenId: initialData?.locationPenId || '',
      active: initialData?.active ?? true,
    });
  }, [initialData, isOpen]);

  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'endDate') setEndDateOverridden(true);
    setFormData((p) => ({
      ...p,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'startDate' && !endDateOverridden ? { endDate: addCalendarDays(value, 120) } : {}),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      batchName: formData.batchName.trim(),
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      totalPurchasePrice: num(formData.totalPurchasePrice),
      marketPricePerKg: num(formData.marketPricePerKg),
      closed: !!formData.closed,
      investorAmount: num(formData.investorAmount),
      notes: formData.notes?.trim() || null,
      species: formData.species ? formData.species.toUpperCase() : null,
      batchGoal: formData.batchGoal ? formData.batchGoal.toUpperCase() : null,
      locationPenId: formData.locationPenId || null,
      active: !!formData.active,
    };

    if (payload.startDate && payload.endDate && payload.endDate <= payload.startDate) {
      toast.error('End Date must be later than Start Date.');
      return;
    }
    onSave(initialData?.id, payload);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{isEditing ? 'Edit Batch' : 'Create New Batch'}</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Batch Name</label>
            <input
              name="batchName"
              value={formData.batchName}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-group" style={{ marginBottom: '1.25rem' }}>
            <label>Batch Eligibility</label>
            <div className="toggle-row">
              <div className="toggle-text">
                {formData.active ? 'Active (Eligible for Automation)' : 'Inactive (Excluded from Automation)'}
              </div>
              <label className="switch" title="If OFF, vaccines will not auto-apply to this batch">
                <input
                  type="checkbox"
                  name="active"
                  checked={!!formData.active}
                  onChange={handleChange}
                />
                <span className="slider" />
              </label>
            </div>
          </div>

          {isEditing && (
            <div className="form-group">
              <label className="form-checkbox">
                <input type="checkbox" name="closed" checked={!!formData.closed} onChange={handleChange} />
                Close this batch (mark as completed)
              </label>
              <div className="helper-text">Closing is separate from automation eligibility. Verify sales and expenses first.</div>
            </div>
          )}
          <div className="form-grid">
            <div className="form-group">
              <label>Species</label>
              <input
                name="species"
                value={formData.species}
                onChange={handleChange}
                className="form-input"
                placeholder="GOAT"
              />
            </div>

            <div className="form-group">
              <label>Batch Goal</label>
              <select
                name="batchGoal"
                value={formData.batchGoal}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select Goal</option>
                {BATCH_GOALS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label>End Date (120 days by default; editable)</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label>Total Goat Purchase Cost (₹)</label>
              <input type="number" min="0" step="0.01" name="totalPurchasePrice"
                value={formData.totalPurchasePrice} onChange={handleChange}
                className="form-input" placeholder="Total paid for the whole batch" required />
            </div>

            <div className="form-group">
              <label>Current Market Price (₹/kg, optional)</label>
              <input type="number" min="0" step="0.01" name="marketPricePerKg"
                value={formData.marketPricePerKg} onChange={handleChange}
                className="form-input" placeholder="Used for estimated livestock value" />
            </div>

            <div className="form-group">
              <label>Investor Amount</label>
              <input
                type="number"
                name="investorAmount"
                value={formData.investorAmount}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / span 2' }}>
              <label>Pen / Location</label>
              <input
                name="locationPenId"
                value={formData.locationPenId}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="form-textarea"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.75rem', marginTop: '1.5rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>

            <button type="submit" className="btn btn-primary">
              {isEditing ? 'Update Batch' : 'Create Batch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// -------------------- TABLE ROW + SKELETON --------------------
const BatchTableRow = ({ batch, selected, onSelect, onEdit, onDelete, onOpenDetails }) => {
  const investment = Number(batch.investment ?? 0);
  const revenue = Number(batch.revenue ?? 0);
  const finalized = batch.financialsFinalized === true;
  const profit = finalized ? batch.finalProfit : null;
  const roi = finalized ? batch.finalRoiPercentage : null;
  const estimated = batch.estimatedLivestockValue;
  const estimatePL = batch.estimatedUnrealizedPl;
  const rupees = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', {maximumFractionDigits: 2})}`;
  const progress = Math.min(Math.max(Number(batch.progressPercent ?? 0), 0), 100);

  const showInactive = batch?.active === false && batch?.status !== 'Completed';

  return (
    <tr key={batch.id}>
      <td>
        <input
          type="checkbox"
          className="select-checkbox"
          checked={!!selected}
          onChange={(e) => onSelect(batch.id, e.target.checked)}
          aria-label={`Select ${batch.batchName}`}
        />
      </td>
      <td>{batch.batchName}</td>

      <td>
        <span className={`badge ${batch.status}`}>{batch.status}</span>
        {showInactive && <span className="badge Inactive">Inactive</span>}
      </td>

      <td>{batch.startDate ? new Date(batch.startDate).toLocaleDateString() : '-'}</td>
      <td>{batch.endDate ? new Date(batch.endDate).toLocaleDateString() : '-'}</td>
      <td>{batch.livestockCount ?? batch.totalCount ?? 0}</td>

      <td>{rupees(investment)}</td>
      <td>{rupees(revenue)}</td>
      <td>{finalized ? rupees(profit) : <span title="Not finalized; livestock or costs may remain">Pending</span>}</td>
      <td>{roi !== null ? `${Number(roi).toFixed(2)}%` : 'Pending'}</td>
      <td title="Unsold weight × entered market price">{rupees(estimated)}
        {estimatePL != null && <small style={{display:'block',opacity:0.75}}>Indicative P/L: {rupees(estimatePL)}</small>}
      </td>

      <td>
        <div className="progress-container">
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>
      </td>

      <td>
        <div className="manage-col">
          <Link to={`/admin/batches/${batch.id}/livestock`} className="btn-view-livestock" title="Open livestock list">
            <ViewIcon /> Livestock
          </Link>

          <Link to={`/admin/batches/${batch.id}/expenses`} className="btn-view-livestock" title="Operating expense ledger">₹ Expenses</Link>
          <button className="btn-details" onClick={() => onOpenDetails(batch)} title="Open batch details">
            <DetailsIcon /> Details
          </button>
        </div>
      </td>

      <td className="action-buttons">
        <button onClick={() => onEdit(batch)} className="action-btn edit" aria-label={`Edit ${batch.batchName}`}>
          <EditIcon />
        </button>
        <button onClick={() => onDelete(batch.id)} className="action-btn delete" aria-label={`Delete ${batch.batchName}`}>
          <DeleteIcon />
        </button>
      </td>
    </tr>
  );
};

const TableSkeleton = ({ columns }) =>
  [...Array(5)].map((_, i) => (
    <tr key={i}>
      {columns.map((_, j) => (
        <td key={j}><div className="skeleton-bar" style={{ height: '24px', width: '80%' }} /></td>
      ))}
    </tr>
  ));

// -------------------- MAIN PAGE --------------------
export default function BatchManagementPage() {
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsBatch, setDetailsBatch] = useState(null);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [batchPeriodFilter, setBatchPeriodFilter] = useState('PRESENT');

  const tableHeaders = useMemo(() => ([
    "",
    "Batch Name",
    "Status",
    "Start",
    "End",
    "Goats",
    "Investment",
    "Revenue",
    "Profit",
    "ROI",
    "Estimated Value",
    "Progress",
    "Manage",
    "Actions"
  ]), []);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await batchService.getEnhancedBatches();
      setBatches(Array.isArray(data) ? data : []);
      setSelectedBatchIds([]);
    } catch (err) {
      setError("Failed to load batches. Please check the backend server and API service.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const classifyBatchPeriod = useCallback((batch) => {
    const status = String(batch?.status || '').toUpperCase();
    if (status === 'COMPLETED') return 'PAST';
    if (status === 'UPCOMING') return 'FUTURE';
    if (status === 'ACTIVE' || status === 'OVERDUE' || status === 'PLANNED') return 'PRESENT';

    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const start = batch?.startDate ? new Date(batch.startDate) : null;
    if (start && todayOnly < start) return 'FUTURE';
    // End-date expiry alone does not close a batch.
    if (batch?.closed === true) return 'PAST';
    return 'PRESENT';
  }, []);

  const periodCounts = useMemo(() => {
    const counts = { PAST: 0, PRESENT: 0, FUTURE: 0 };
    batches.forEach((batch) => {
      const key = classifyBatchPeriod(batch);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [batches, classifyBatchPeriod]);

  const filteredBatches = useMemo(
    () => batches.filter((batch) => classifyBatchPeriod(batch) === batchPeriodFilter),
    [batches, batchPeriodFilter, classifyBatchPeriod]
  );

  const visibleBatchIds = useMemo(() => filteredBatches.map((b) => b.id), [filteredBatches]);
  const allVisibleSelected = visibleBatchIds.length > 0 && visibleBatchIds.every((id) => selectedBatchIds.includes(id));

  const toggleSelectBatch = (id, checked) => {
    setSelectedBatchIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAllVisible = (checked) => {
    setSelectedBatchIds((prev) => {
      if (!checked) return prev.filter((id) => !visibleBatchIds.includes(id));
      return Array.from(new Set([...prev, ...visibleBatchIds]));
    });
  };

  const handleSaveBatch = async (id, data) => {
    try {
      const op = id ? batchService.updateBatch(id, data) : batchService.createBatch(data);
      await op;
      setIsModalOpen(false);
      await loadBatches();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save batch. See console for details.");
    }
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const impact = await batchService.getDeleteImpact(id);
      const linked = Object.entries(impact?.linked || {}).filter(([, count]) => Number(count) > 0);
      const detail = linked.length
        ? `\n\nLinked records that will also be permanently deleted:\n${linked.map(([name, count]) => `• ${name.replace(/([A-Z])/g, " $1")}: ${count}`).join("\n")}`
        : "\n\nNo linked records were found.";
      if (!window.confirm(`Delete batch “${impact?.batchName || id}”?${detail}\n\nThis action cannot be undone.`)) return;
      await batchService.deleteBatch(id, true);
      setSelectedBatchIds((prev) => prev.filter((x) => x !== id));
      await loadBatches();
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessage(err, "Failed to delete batch. Please check linked records or backend logs."));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedBatchIds.length) return;
    try {
      const impacts = await Promise.all(selectedBatchIds.map((id) => batchService.getDeleteImpact(id)));
      const totals = {};
      impacts.forEach((impact) => Object.entries(impact?.linked || {}).forEach(([key, value]) => {
        totals[key] = (totals[key] || 0) + Number(value || 0);
      }));
      const linked = Object.entries(totals).filter(([, count]) => count > 0);
      const detail = linked.length
        ? `\n\nCombined linked records that will also be deleted:\n${linked.map(([name, count]) => `• ${name.replace(/([A-Z])/g, " $1")}: ${count}`).join("\n")}`
        : "\n\nNo linked records were found.";
      if (!window.confirm(`Permanently delete ${selectedBatchIds.length} selected batch(es)?${detail}\n\nThis action cannot be undone.`)) return;
      await batchService.deleteBatches(selectedBatchIds);
      setSelectedBatchIds([]);
      await loadBatches();
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessage(err, "Failed to delete selected batches. Please check linked records or backend logs."));
    }
  };

  const handleOpenCreateModal = () => {
    setEditingBatch(null);
    setIsModalOpen(true);
  };

  const handleOpenDetails = (batch) => {
    setDetailsBatch(batch);
    setDetailsOpen(true);
  };

  const handleOpenLivestockPage = (batch, livestockItem = null) => {
    const batchId = batch?.id;
    if (!batchId) return;

    navigate(`/admin/batches/${batchId}/livestock`, {
      state: livestockItem ? { highlightLivestockId: livestockItem.id } : undefined,
    });
    setDetailsOpen(false);
  };

  return (
    <>
      <Styles />

      <div className="page-header">
        <h1 className="page-title">Livestock Batches</h1>
        <div style={{ display: 'flex', gap: '.75rem' }}>
          <button onClick={loadBatches} className="btn btn-secondary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
            </svg>
            Refresh
          </button>

          <button onClick={handleOpenCreateModal} className="btn btn-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Create New Batch
          </button>
        </div>
      </div>

      <div className="category-tabs" role="tablist" aria-label="Batch period filter">
        {[
          ['PAST', 'Past'],
          ['PRESENT', 'Present'],
          ['FUTURE', 'Future'],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`category-tab ${batchPeriodFilter === key ? 'active' : ''}`}
            onClick={() => {
              setBatchPeriodFilter(key);
              setSelectedBatchIds([]);
            }}
          >
            {label} <span className="record-pill">{periodCounts[key] || 0}</span>
          </button>
        ))}
      </div>

      {selectedBatchIds.length > 0 && (
        <div className="bulk-toolbar">
          <div className="bulk-count">{selectedBatchIds.length} batch(es) selected</div>
          <button className="btn btn-danger" onClick={handleBulkDelete}>
            <DeleteIcon /> Delete Selected
          </button>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>{tableHeaders.map((h, idx) => (
              <th key={idx || h}>
                {idx === 0 ? (
                  <input
                    type="checkbox"
                    className="select-checkbox"
                    checked={allVisibleSelected}
                    disabled={!visibleBatchIds.length}
                    onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                    aria-label="Select all visible batches"
                  />
                ) : h}
              </th>
            ))}</tr>
          </thead>

          <tbody>
            {loading ? (
              <TableSkeleton columns={tableHeaders} />
            ) : filteredBatches.length > 0 ? (
              filteredBatches.map(b => (
                <BatchTableRow
                  key={b.id}
                  batch={b}
                  selected={selectedBatchIds.includes(b.id)}
                  onSelect={toggleSelectBatch}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onOpenDetails={handleOpenDetails}
                />
              ))
            ) : (
              <tr>
                <td colSpan={tableHeaders.length} className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ opacity: 0.5, marginBottom: '1rem' }}>
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                  <h3>No {batchPeriodFilter.toLowerCase()} batches found</h3>
                  <p>Change the Past / Present / Future filter or create a new batch.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <BatchFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveBatch}
        initialData={editingBatch}
      />

      <OverallDetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        batch={detailsBatch}
        onOpenLivestockPage={handleOpenLivestockPage}
      />
    </>
  );
}
/* Farm OS: keep batch identity visible when scrolling large tables. */
